/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  initialQueryState,
  PaginationState,
  QUERIES,
  WithChildren,
  Response,
} from '../../../../../_dashboard/helpers'
import {useQueryRequest} from './QueryRequestProvider'
import {getAll} from '../../../../services/common_requests'
import {createTourResponseContext, Tour, initialTourQueryResponse} from './_models'
import {buildToursListQuery} from './buildToursListQuery'

interface QueryResponseContextProps {
  dataResponse?: Response<Tour[]>
  refetch: () => void
  query: any
  pagination: PaginationState
  isLoading: boolean
  isError: boolean
  setQuery: (query: any) => void
  setPageSize: (pageSize: number) => void
  setCurrentPage: (page: number) => void
  refetchData: () => void
}

const QueryTourResponseContext = createTourResponseContext<Tour>(initialTourQueryResponse)

const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const apiQuery = useMemo(() => buildToursListQuery(state), [state])

  const {
    isFetching: isFetchingTours,
    refetch,
    data: dataResponse,
  } = useQuery<Response<Tour[]>>(
    `${(QUERIES as any).TOURS_LIST}-${JSON.stringify(apiQuery)}`,
    () => getAll(apiQuery, '/tours/list'),
    {
      cacheTime: 0,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
  )

  const value = {
    dataResponse,
    refetch,
    query: apiQuery,
    pagination: dataResponse?.payload?.pagination || {
      totalPages: 0,
      currentPage: 0,
      pageSize: 10,
      totalItems: 0,
    },
    isLoading: isFetchingTours,
    isError: false,
    setQuery: () => {},
    setPageSize: () => {},
    setCurrentPage: () => {},
    refetchData: refetch,
  }

  return (
    <QueryTourResponseContext.Provider value={value}>
      {children}
    </QueryTourResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryTourResponseContext)

const useQueryResponseData = () => {
  const {dataResponse} = useQueryResponse()
  if (!dataResponse) return []
  return dataResponse.data || []
}

const useQueryResponsePagination = (): PaginationState => {
  const defaultPagination: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {dataResponse} = useQueryResponse()
  if (!dataResponse || !dataResponse.payload) {
    return defaultPagination
  }

  return {
    links: [],
    ...initialQueryState,
    ...dataResponse.payload.pagination,
  }
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
